import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { PrimaryButton, SecondaryButton } from './Button'
import Checkbox from './Checkbox'
import Dropdown, { DropdownItem } from './Dropdown'
import { InputLabel, TextInputWithLabel } from './Input'
import Modal, { ModalProps } from './Modal'
import Radom, { Customer, DepositCryptoAddressType } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { DepositMethods, prodNetworks, testNetworks } from '../util/Managed'
import { errorToast, successToast, useTestMode } from '../util/Util'
import CustomerForm from './CustomerForm'
import SearchMultiselect from './SearchMultiselect'
import styled from 'styled-components'
import User from '../state/User'

interface Leaf { network: string, token?: string, name: string, logo: string, ticker?: string }

interface Selected { network: string, token: string | undefined }

interface ICreateDepositAddressModal extends ModalProps {
  onAccountCreated: () => void
}

const NATIVE = 'NATIVE'

const AddButton = styled.div`
  width: 100%;
  padding: 14px;
  color: ${RADOM_COLORS.BLACK};
  cursor: pointer;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }
`
const CreateDepositAddressModal = observer(({
  title,
  onAccountCreated,
  onClose,
  visible
}: ICreateDepositAddressModal): ReactElement => {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customerFormVisible, setCustomerFormVisible] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [accountName, setAccountName] = useState('')
  const [selectedDepositMethods, setSelectedDepositMethods] =
    useState<Selected[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [testMode] = useTestMode()
  const [depositAddressType, setDepositCryptoAddressType] =
    useState<DepositCryptoAddressType>(DepositCryptoAddressType.FirstParty)

  const [shouldHideMonero, setShouldHideMonero] = useState(true);
    
  useEffect(() => {
    User.getOrganization().then((o) => setShouldHideMonero(!o.features?.isMoneroEnabled))
  }, []);

  const loadCustomers = async (): Promise<void> => {
    Radom.listCustomers(0, 100, testMode ? testNetworks : prodNetworks)
      .then(c => {
        setCustomers(c.data)
      }).then(() => setCustomerFormVisible(false))
  }

  const createAddress = async (): Promise<void> => {
    if (selectedDepositMethods.length <= 0) {
      return
    }
    const isExternal = depositAddressType === DepositCryptoAddressType.ThirdParty
    const customerId = isExternal && selectedCustomer ? selectedCustomer.id : undefined
    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Crypto: {
            depositMethods: selectedDepositMethods
              .map(m => ({
                network: m.network,
                token: m.token
              })),
            isExternal,
            customerId
          }
        }
      })
      onAccountCreated()
      successToast('Deposit address created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const leafKey = (x: Selected | Leaf): string => `${x.network}:${x.token ?? NATIVE}`

  const collectLeaves = (node: any): Leaf[] => {
    if (node?.method) {
      return [{
        network: node.method.network,
        token: node.method.token,
        name: node.name,
        logo: node.logo,
        ticker: node.ticker
      }]
    }
    if (node?.subMethods && node.subMethods.length > 0) {
      return node.subMethods.flatMap(collectLeaves)
    }
    if (node?.name) {
      return [{
        network: node.name,
        token: undefined,
        name: node.name,
        logo: node.logo,
        ticker: node.ticker
      }]
    }
    return []
  }

  const getCheckState = (leaves: Leaf[], selectedSet: Set<string>): { all: boolean, partial: boolean } => {
    const total = leaves.length
    const sel = leaves.reduce((a, l) => a + (selectedSet.has(leafKey(l as Selected)) ? 1 : 0), 0)
    const all = total > 0 && sel === total
    const none = sel === 0
    return { all, partial: !none && !all }
  }

  const addAll = (selected: Selected[], leaves: Leaf[]): Selected[] => {
    const s = new Set(selected.map(leafKey))
    leaves.forEach(l => s.add(leafKey(l as Selected)))
    return Array.from(s).map(k => {
      const [network, tok] = k.split(':')
      return { network, token: tok === NATIVE ? undefined : tok }
    })
  }

  const removeAll = (selected: Selected[], leaves: Leaf[]): Selected[] => {
    const r = new Set(leaves.map(l => leafKey(l as Selected)))
    return selected.filter(x => !r.has(leafKey(x)))
  }

  const toggleLeaf = (selected: Selected[], leaf: Leaf): Selected[] => {
    const k = leafKey(leaf as Selected)
    const exists = selected.some(s => leafKey(s) === k)
    return exists ? selected.filter(s => leafKey(s) !== k) : [...selected, { network: leaf.network, token: leaf.token }]
  }

  const topGroups = useMemo(
    () => DepositMethods.filter(d => (testMode ? d.isTestnet : !d.isTestnet)).filter((d) => {
      if(d.name !== "Monero" && d.name !== "MoneroTestnet") {
        return true;
      }
      return !shouldHideMonero;
    }),
    [testMode, shouldHideMonero]
  )
  const selectedSet = useMemo(() => new Set(selectedDepositMethods.map(leafKey)), [selectedDepositMethods])
  const selectedLeafCount = useMemo(() => selectedDepositMethods.length, [selectedDepositMethods])

  useEffect(() => {
    loadCustomers()
  }, [])

  return <Modal
    title={title}
    visible={visible}
    onClose={onClose}
    innerStyles={{ width: 550 }}>

    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 10,
      padding: '0px 30px'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InputLabel>Who will own this account?</InputLabel>
          <div style={{ fontSize: 14, display: 'flex', justifyContent: 'space-between' }}>
            <SecondaryButton
              disabled={isLoading}
              onClick={() => {
                setDepositCryptoAddressType(DepositCryptoAddressType.FirstParty)
                if (selectedCustomer) {
                  setSelectedCustomer(null)
                }
              }} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: 0,
                padding: 5,
                color: depositAddressType ===
                  DepositCryptoAddressType.FirstParty
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox checked={depositAddressType === DepositCryptoAddressType.FirstParty} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{DepositCryptoAddressType.FirstParty}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                            Owned by your business
                </span>
              </div>
            </SecondaryButton>

            <SecondaryButton
              disabled={isLoading}
              onClick={() => {
                setDepositCryptoAddressType(DepositCryptoAddressType.ThirdParty)
                if (selectedCustomer) {
                  setSelectedCustomer(null)
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: 0,
                padding: 5,
                color: depositAddressType ===
                  DepositCryptoAddressType.ThirdParty
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox checked={depositAddressType === DepositCryptoAddressType.ThirdParty} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{DepositCryptoAddressType.ThirdParty}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                            Created for a customer
                </span>
              </div>
            </SecondaryButton>
          </div>
        </div>

        <TextInputWithLabel
          label='Account nickname'
          placeholder='Enter a name for this account'
          disabled={isLoading}
          required
          value={accountName}
          onChange={(e) => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setAccountName(value)
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InputLabel>Deposit methods</InputLabel>
          <Dropdown
            innerStyle={{ maxHeight: 300 }}
            disabled={isLoading}
            selectedContent={
              selectedLeafCount > 0
                ? <span style={{ fontSize: 14 }}>{selectedLeafCount} methods selected</span>
                : <span style={{ fontSize: 14 }}>Select deposit methods</span>
            }
            dropdownContent={
              <div onMouseDown={(e) => e.stopPropagation()}>
                {topGroups.map((group, idx) => {
                  const groupLeaves = collectLeaves(group)
                  const groupState = getCheckState(groupLeaves, selectedSet)

                  return (
                    <div key={`g-${idx}`}>
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDepositMethods(prev => groupState.all
                            ? removeAll(prev, groupLeaves)
                            : addAll(prev, groupLeaves))
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Checkbox checked={groupState.all} />
                          <div
                            style={{
                              height: 25,
                              width: 25,
                              backgroundImage: `url(${group.logo})`,
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              position: 'relative'
                            }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: 14 }}>{group.name}</span>
                            {group.description &&
                            <span style={{ fontSize: 12, opacity: 0.5 }}>{group.description}</span>}
                          </div>
                        </div>
                      </DropdownItem>

                      {(group.subMethods ?? []).map((net: any, jdx: number) => {
                        const netLeaves = collectLeaves(net)
                        const netState = getCheckState(netLeaves, selectedSet)
                        const isLeafNode = !!net?.method

                        if (isLeafNode) {
                          const leaf = netLeaves[0]
                          const leafState = getCheckState([leaf], selectedSet)
                          return (
                            <div key={`l-${idx}-${jdx}`}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 45 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                  <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                                </div>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedDepositMethods(prev => toggleLeaf(prev, leaf))
                                  }}
                                  style={{ width: '100%' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Checkbox checked={leafState.all} />
                                    <div
                                      style={{
                                        height: 25,
                                        width: 25,
                                        backgroundImage: `url(${leaf.logo})`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'contain',
                                        backgroundPosition: 'center',
                                        position: 'relative'
                                      }} />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span style={{ fontSize: 14 }}>{leaf.name}</span>
                                      {leaf.ticker && <span style={{ fontSize: 12, opacity: 0.5 }}>{leaf.ticker}</span>}
                                    </div>
                                  </div>
                                </DropdownItem>
                              </div>
                            </div>
                          )
                        }

                        return (
                          <div key={`n-${idx}-${jdx}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 45 }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                              </div>
                              <DropdownItem
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedDepositMethods(prev =>
                                    netState.all
                                      ? removeAll(prev, netLeaves)
                                      : addAll(prev, netLeaves))
                                }}
                                style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <Checkbox checked={netState.all} />
                                  <div
                                    style={{
                                      height: 25,
                                      width: 25,
                                      backgroundImage: `url(${net.logo})`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundSize: 'contain',
                                      backgroundPosition: 'center',
                                      position: 'relative'
                                    }} />
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: 14 }}>{net.name}</span>
                                    {net.ticker && <span style={{ fontSize: 12, opacity: 0.5 }}>{net.ticker}</span>}
                                  </div>
                                </div>
                              </DropdownItem>
                            </div>

                            {(net.subMethods ?? []).map((tok: any, kdx: number) => {
                              const tokenLeaves = collectLeaves(tok)
                              const leaf = tokenLeaves[0]
                              const leafState = getCheckState(tokenLeaves, selectedSet)

                              return (
                                <div key={`t-${idx}-${jdx}-${kdx}`} style={{ display: 'flex', alignItems: 'center', gap: 4, height: 48 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                    <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: 25, height: '100%' }}>
                                    <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                    <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  </div>

                                  <DropdownItem
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSelectedDepositMethods(prev => toggleLeaf(prev, leaf))
                                    }}
                                    style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <Checkbox checked={leafState.all} />
                                      <div
                                        style={{
                                          height: 25,
                                          width: 25,
                                          backgroundImage: `url(${tok.logo})`,
                                          backgroundRepeat: 'no-repeat',
                                          backgroundSize: 'contain',
                                          backgroundPosition: 'center',
                                          position: 'relative'
                                        }} />
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontSize: 14 }}>{tok.name}</span>
                                        {tok.ticker && <span style={{ fontSize: 12, opacity: 0.5 }}>{tok.ticker}</span>}
                                      </div>
                                    </div>
                                  </DropdownItem>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            }
          />
        </div>
        { depositAddressType === DepositCryptoAddressType.ThirdParty &&
         <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
           <InputLabel>Customer</InputLabel>
           <SearchMultiselect
             isSingleSelect={true}
             isLoading={isLoading}
             placeholder="Select customer"
             dropdownPlaceholder="No customers found"
             itemArray={customers}
             selectedItems={selectedCustomer ? [selectedCustomer] : []}
             setSelectedItems={newItems => {
               setSelectedCustomer(newItems[0])
             }}
             headers={[
               <AddButton key="create_customer" onClick={() => setCustomerFormVisible(true)}>
                 <span>+ Create customer</span>
               </AddButton>
             ]}
             keyExtractor={(customer) => `${customer.id}${customer.name}${customer.email}`}
             labelExtractor={customer => customer.name ? `${customer.name} (${customer.email})` : customer.email}
             onNewItem={() => setCustomerFormVisible(true)}
           />
         </div>}
      </div>
    </div>

    <div style={{
      padding: 30,
      borderRadius: 15,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0
    }}>
      <PrimaryButton
        isLoading={isLoading}
        disabled={!accountName || selectedDepositMethods.length <= 0 ||
           (depositAddressType === DepositCryptoAddressType.ThirdParty && !selectedCustomer)}
        style={{ width: '100%' }}
        onClick={() => {
          createAddress()
        }}>
          Create deposit account
      </PrimaryButton>
    </div>

    <Modal
      visible={customerFormVisible}
      onClose={() => setCustomerFormVisible(false)}
      title='Create customer'>
      <div style={{ padding: '0px 30px 30px 30px', overflow: 'scroll' }}>
        <CustomerForm onCreateCustomer={loadCustomers} />
      </div>
    </Modal>
  </Modal>
})

export default CreateDepositAddressModal
